import { z } from 'zod';

import { dateSchema, localDay, shiftDay } from './calendar';
import { focusSchema } from './focus';
import { referencedAssets } from './media';
import type { EntityValue, Kind, Note, RecordData, Task } from './model';
import { isAvailableRecord } from './trash';

export const goalSchema = z.object({
  title: z.string().trim().min(1).max(500),
  target: z.number().positive().max(1e9),
  unit: z.string().max(100),
  weeklyTarget: z.number().positive().nullable(),
  taskIds: z.array(z.string().uuid()).max(10000),
  status: z.enum(['active', 'completed', 'archived']),
  completedAt: z.string().datetime().nullable(),
  noteId: z.string().uuid().nullable(),
});

export const progressSchema = z.object({
  goalId: z.string().uuid(),
  date: dateSchema,
  amount: z.number().positive().max(1e9),
  taskId: z.string().uuid().nullable(),
  note: z.string().max(5000),
  reversed: z.boolean(),
});

export const reviewSchema = z.object({
  week: dateSchema,
  completedAt: z.string().datetime().nullable(),
  reflection: z.string().max(20000),
  decisions: z.string().max(20000),
});

export const personSchema = z.object({
  name: z.string().trim().min(1).max(500),
  contact: z.string().max(2000),
  notes: z.string().max(20000),
  archived: z.boolean(),
  gifts: z
    .array(
      z.object({
        id: z.string().uuid(),
        title: z.string().trim().min(1).max(500),
        date: dateSchema.nullable(),
        status: z.enum(['idea', 'given']),
        notes: z.string().max(5000),
      }),
    )
    .max(2000),
});

export type Goal = z.infer<typeof goalSchema>;
export type Progress = z.infer<typeof progressSchema>;
export type Review = z.infer<typeof reviewSchema>;
export type Person = z.infer<typeof personSchema>;
export type Change = { id: string; kind: Kind; value: EntityValue; deleted?: boolean };
export type RecordLike = Pick<RecordData, 'id' | 'kind' | 'value'>;

export function taskPersonName(task: Task, records: RecordLike[]) {
  const person = records.find(r => r.kind === 'person' && r.id === task.personId);

  return person ? (person.value as Person).name : '';
}

export type ProjectChoice = { id: string; title: string; nested: boolean };

/**
 * Live PARA Projects for the task and vault pickers, by title.
 *
 * A Project filed under another live Project is `nested`: a subproject keeps
 * its page in the library, but pickers offer only top-level Projects. One whose
 * parent is gone counts as top-level, the way the library shows it.
 */
export function projectChoices(records: (RecordLike & { deleted?: boolean })[]): ProjectChoice[] {
  const projects = records.filter(
    r => r.kind === 'note' && isAvailableRecord(r) && (r.value as Note).area === 'Projects',
  );

  const ids = new Set(projects.map(r => r.id));

  return projects
    .map(r => {
      const note = r.value as Note;

      return {
        id: r.id,
        title: note.title || 'Untitled project',
        nested: Boolean(note.parentId && ids.has(note.parentId)),
      };
    })
    .sort((a, b) => a.title.localeCompare(b.title) || a.id.localeCompare(b.id));
}

export function weekStart(day = localDay()) {
  const date = new Date(`${day}T12:00:00Z`);

  return shiftDay(day, -(date.getUTCDay() + 6) % 7);
}

export function goalProgress(goalId: string, records: RecordLike[], week?: string) {
  const goal = records.find(r => r.id === goalId && r.kind === 'goal')?.value as Goal | undefined;

  if (!goal) {
    return 0;
  }

  const until = week ? shiftDay(week, 7) : undefined;
  const inPeriod = (day?: string | null) => !week || Boolean(day && day >= week && day < until!);

  const entries = records
    .filter(
      r =>
        r.kind === 'progress' &&
        (r.value as Progress).goalId === goalId &&
        !(r.value as Progress).reversed,
    )
    .map(r => r.value as Progress);

  let total = entries
    .filter(e => !e.taskId && inPeriod(e.date))
    .reduce((sum, e) => sum + e.amount, 0);

  const linked = new Set([...goal.taskIds, ...entries.flatMap(e => (e.taskId ? [e.taskId] : []))]);

  for (const taskId of linked) {
    const task = records.find(r => r.kind === 'task' && r.id === taskId)?.value as Task | undefined;

    if (!task?.done) {
      continue;
    }

    const override = entries.find(e => e.taskId === taskId);

    if (override) {
      if (inPeriod(override.date)) {
        total += override.amount;
      }
    } else if (inPeriod(task.completedOn)) {
      total += 1;
    }
  }

  return total;
}

export function validateProductivity(records: RecordLike[]) {
  const seen = new Set<string>();

  if (records.filter(r => r.kind === 'settings').length > 1) {
    throw new Error('Only one workspace timezone setting is allowed.');
  }

  const weeks = records.filter(r => r.kind === 'review').map(r => (r.value as Review).week);

  if (new Set(weeks).size !== weeks.length || weeks.some(w => weekStart(w) !== w)) {
    throw new Error('Reviews need one Monday-start record per week.');
  }

  const exists = (id: string | null | undefined, kind: Kind) =>
    !id || records.some(r => r.id === id && r.kind === kind);

  for (const record of records) {
    if (record.kind === 'note') {
      const note = record.value as Note;

      if (referencedAssets(note.content).some(id => !exists(id, 'asset'))) {
        throw new Error('A note image has not finished downloading.');
      }

      if (!exists(note.parentId, 'note')) {
        throw new Error('The parent note is unavailable.');
      }

      const seen = new Set([record.id]);
      let parent = note.parentId;

      while (parent) {
        if (seen.has(parent)) {
          throw new Error('A note cannot be its own ancestor.');
        }

        seen.add(parent);
        parent = (records.find(r => r.id === parent)?.value as Note | undefined)?.parentId ?? null;
      }
    }

    if (record.kind === 'focus' && !exists(focusSchema.parse(record.value).taskId, 'task')) {
      throw new Error('Focus session links to an unavailable task.');
    }

    if (record.kind === 'goal') {
      const goal = goalSchema.parse(record.value);

      if (!goal.taskIds.every(id => exists(id, 'task')) || !exists(goal.noteId, 'note')) {
        throw new Error('A goal links to an unavailable task or note.');
      }
    }

    if (record.kind === 'progress') {
      const progress = progressSchema.parse(record.value);

      if (!exists(progress.goalId, 'goal') || !exists(progress.taskId, 'task')) {
        throw new Error('Progress links to an unavailable goal or task.');
      }

      if (progress.taskId && !progress.reversed) {
        const key = `${progress.goalId}:${progress.taskId}`;

        if (seen.has(key)) {
          throw new Error(
            'This task already has a progress entry for this goal. Edit or reverse that entry first.',
          );
        }

        seen.add(key);
      }
    }

    if (record.kind === 'task') {
      const task = record.value as Task;

      if (
        !exists(task.personId, 'person') ||
        !exists(task.projectId, 'note') ||
        !exists(task.areaId, 'note')
      ) {
        throw new Error('The task context is unavailable.');
      }
    }
  }
}

export function expandChanges(
  records: RecordLike[],
  changes: Change[],
  today = localDay(),
): Change[] {
  const result = new Map(changes.map(change => [change.id, { ...change }]));

  for (const change of result.values()) {
    if (change.kind === 'task' && !change.deleted) {
      const next = change.value as Task,
        previous = records.find(r => r.id === change.id)?.value as Task | undefined;

      change.value = {
        ...next,
        completedOn: next.done ? (previous?.done ? (previous.completedOn ?? today) : today) : null,
      };
    }
  }

  let projected = [...records.filter(r => !result.has(r.id)), ...result.values()];

  for (const record of projected) {
    if (record.kind === 'goal') {
      const goal = record.value as Goal;

      if (goal.status === 'active' && goalProgress(record.id, projected) >= goal.target) {
        result.set(record.id, {
          ...record,
          value: { ...goal, status: 'completed', completedAt: new Date().toISOString() },
        });
      }
    }
  }

  projected = [...records.filter(r => !result.has(r.id)), ...result.values()];
  validateProductivity(projected);

  return [...result.values()];
}

export function taskSuggestions(records: RecordLike[], today = localDay()) {
  const people = new Map(
    records.filter(r => r.kind === 'person').map(r => [r.id, (r.value as Person).name]),
  );

  return records
    .filter(
      r =>
        r.kind === 'task' &&
        !(r.value as Task).done &&
        !(r.value as Task).dropped &&
        !(r.value as Task).trashed,
    )
    .map(record => {
      const task = record.value as Task,
        waiting = task.personId ? people.get(task.personId) : '';

      const near = Boolean(task.dueDate && task.dueDate <= shiftDay(today, 7));

      return {
        ...record,
        reason: near
          ? task.dueDate! < today
            ? `Overdue since ${task.dueDate}`
            : `Due ${task.dueDate}`
          : waiting
            ? `${waiting} is waiting`
            : task.dueDate
              ? `Due ${task.dueDate}`
              : 'A concrete next step',
        rank: near ? 0 : waiting ? 1 : task.dueDate ? 2 : 3,
      };
    })
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        ((a.value as Task).dueDate ?? '9999').localeCompare((b.value as Task).dueDate ?? '9999') ||
        (a.value as Task).title.localeCompare((b.value as Task).title),
    );
}
